import {StagingTitelineAppDrilling} from "../types/StagingTitelineAppDrilling";
import {DrillingData} from "../types/DrillingData";
import { getApiUrl } from '../config/api';

export const fetchDrillingByPid = async (pid: number, aid: number = -1): Promise<DrillingData[]> => {
    const validPid = pid ?? -1;
    const apiUrl = getApiUrl(`/api/TitelineDrilling/GetDrillingByPid/${validPid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.log("Failed to fetch drilling data by pid:", pid, "state:", response.status, response.statusText);
            return [];
        }

        let backendData: StagingTitelineAppDrilling[] = await response.json();
        if (pid === -1) {
            backendData = backendData.filter((drilling) => drilling.DataSource === aid.toString());
        }

        const transformedData: DrillingData[] = backendData.map((drilling) => ({
            key: drilling.Did,
            did: drilling.Did,
            pid: drilling.Pid,
            hole_id: drilling.HoleID,
            angle: drilling.Angle,
            drill_type: drilling.DrillType,
            size: drilling.Size,
            depth_from: drilling.DepthFrom,
            depth_to: drilling.DepthTo,
            total_m: drilling.TotalMetres,
            barrel: drilling.Barrel,
            recovered_metres: drilling.RecoveredMetres,
            dci_metres: drilling.DCIMetres,
            non_chargeable_metres: drilling.NonChargeableMetres,
        }));

        return transformedData;
    } catch (error) {
        console.error("Error in fetchDrillingByPid API call:", error);
        return [];
    }
};

export const addDrillingRow = async (drillingRow: DrillingData, aid: number = -1) => {
    const apiUrl = getApiUrl('/api/TitelineDrilling/AddDrilling');

    const cloudPayload = [
        {
            Pid: drillingRow.pid ?? -1,
            HoleID: drillingRow.hole_id,
            Angle: drillingRow.angle,
            DrillType: drillingRow.drill_type,
            Size: drillingRow.size,
            DepthFrom: drillingRow.depth_from,
            DepthTo: drillingRow.depth_to,
            TotalMetres: drillingRow.total_m,
            Barrel: drillingRow.barrel,
            RecoveredMetres: drillingRow.recovered_metres,
            DCIMetres: drillingRow.dci_metres,
            NonChargeableMetres: drillingRow.non_chargeable_metres,
            DataSource: aid,
        },
    ];

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(cloudPayload),
        });

        if (response.ok) {
            console.log("Drilling row added to cloud successfully.");
            return true;
        } else {
            console.error(
                "Failed to add drilling row to cloud:",
                response.status,
                response.statusText
            );
            return false;
        }
    } catch (error) {
        console.error("Error in addDrillingRow API call:", error);
        return false;
    }
};

export const updateDrillingRow = async (did: number, updatedRow: DrillingData) => {
    const apiUrl = getApiUrl(`/api/TitelineDrilling/UpdateDrillingByDid/${did}`);

    const cloudPayload = {
        HoleID: updatedRow.hole_id,
        Angle: updatedRow.angle,
        DrillType: updatedRow.drill_type,
        Size: updatedRow.size,
        DepthFrom: updatedRow.depth_from,
        DepthTo: updatedRow.depth_to,
        TotalMetres: updatedRow.total_m,
        Barrel: updatedRow.barrel,
        RecoveredMetres: updatedRow.recovered_metres,
        DCIMetres: updatedRow.dci_metres,
        NonChargeableMetres: updatedRow.non_chargeable_metres,
    };

    console.log("cloudPayload:", cloudPayload);

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(cloudPayload),
        });

        if (response.ok) {
            console.log(`Drilling row with Did ${did} updated successfully.`);
            return true;
        } else {
            console.error(
                `Failed to update drilling row with Did ${did}:`,
                response.status,
                response.statusText
            );
            return false;
        }
    } catch (error) {
        console.error("Error in updateDrillingRow API call:", error);
        return false;
    }
};

export const deleteDrillingByDid = async (did: number) => {
    const apiUrl = getApiUrl(`/api/TitelineDrilling/DeleteDrillingByDid/${did}`);

    try {
        const response = await fetch(apiUrl, {
            method: "DELETE",
        });

        if (response.ok) {
            const responseData = await response.json();
            console.log("DeleteDrillingByDid API response:", responseData);
            return true;
        } else {
            console.error("Failed to delete drilling row from the cloud:", response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error("Error in deleteDrillingByDid API call:", error);
        return false;
    }
};

export const fetchTotalDrillingMetresAndCoreLost = async (pid?: number, aid: number = 0): Promise<{ totalMetres: number; totalCoreLost: number }> => {
    const validPid = pid ?? -1;
    const apiUrl = getApiUrl(`/api/TitelineDrilling/CalculateTotalDrillingMeterAndCoreLost/${validPid}/${aid}`);

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Failed to fetch total drilling hours. Status: ${response.status} - ${response.statusText}. Error: ${errorText}`);
            return { totalMetres: 0, totalCoreLost: 0 };
        }

        const data = await response.json();

        return {
            totalMetres: data?.total_drilling_metres ?? 0,
            totalCoreLost: data?.total_core_lost ?? 0,
        };
    } catch (error) {
        console.error("fetch Total Drilling Metres And Core Lost error:", error);
        return { totalMetres: 0, totalCoreLost: 0 };
    }
};
